import { createDataApiFullUrl } from './DataApiClient';

/**
 * Client for fetching global stats and market-level data from the Data API.
 * These endpoints are not user-specific and don't fit the short-term/long-term pattern.
 *
 * For user-specific paginated history data, use DataApiClient instead.
 */
class StatsApiClient {
	/**
	 * Fetch insurance fund stats from the data API
	 * Endpoint: /stats/insuranceFund
	 */
	public static async fetchInsuranceFundStats(): Promise<{
		success: boolean;
		data?: {
			totalRevenue: string;
			perpLiqsTotal: string;
			spotLiqsTotal: string;
			marketSharePriceData: Array<{
				marketIndex: number;
				apy: string | null;
			}>;
		};
	}> {
		try {
			const fullUrl = createDataApiFullUrl('/stats/insuranceFund', '');
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: true,
				data: body.data ?? body,
			};
		} catch (e) {
			console.error('Error fetching insurance fund stats from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch funding rates from the data API
	 * Endpoint: /market/:symbol/fundingRates
	 */
	public static async fetchFundingRates(
		symbol: string,
		limit = 750
	): Promise<{
		success: boolean;
		records?: Array<{
			ts: number;
			marketIndex: number;
			fundingRate: string;
			fundingRateLong: string;
			fundingRateShort: string;
			oraclePriceTwap: string;
			markPriceTwap: string;
		}>;
		meta?: { nextPage: string | null };
	}> {
		try {
			const queryParams = new URLSearchParams();
			queryParams.set('limit', limit.toString());

			const queryString = queryParams.toString();
			const fullUrl = createDataApiFullUrl(
				`/market/${symbol}/fundingRates`,
				`?${queryString}`
			);

			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				records: body.records ?? [],
				meta: body.meta ?? { nextPage: null },
			};
		} catch (e) {
			console.error('Error fetching funding rates from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch market summary (24h data for all markets)
	 * Endpoint: GET /stats/markets
	 */
	public static async fetchMarketSummary(): Promise<{
		success: boolean;
		markets?: Array<{
			symbol: string;
			marketIndex: number;
			marketType: string;
			oraclePrice: string;
			markPrice: string;
			baseVolume: string;
			quoteVolume: string;
			deposits?: string;
			borrows?: string;
			openInterest?: { long: string; short: string };
			fundingRate?: { long: string; short: string };
			fundingRate24h?: string;
			fundingRateUpdateTs?: number;
			price: string;
			priceChange24h: string;
			priceChange24hPercent: string;
			priceHigh?: { oracle: string; fill: string };
			priceLow?: { oracle: string; fill: string };
		}>;
	}> {
		try {
			const fullUrl = createDataApiFullUrl('/stats/markets', '');
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				markets: body.markets ?? [],
			};
		} catch (e) {
			console.error('Error fetching market summary from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch average funding rates for all markets
	 * Endpoint: GET /stats/fundingRates
	 */
	public static async fetchAvgFundingRates(): Promise<{
		success: boolean;
		markets?: Array<{
			marketIndex: number;
			symbol: string;
			fundingRates: {
				'24h': string;
				'7d': string;
				'30d': string;
				'1y': string;
			};
		}>;
	}> {
		try {
			const fullUrl = createDataApiFullUrl('/stats/fundingRates', '');
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				markets: body.markets ?? [],
			};
		} catch (e) {
			console.error('Error fetching avg funding rates from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch bankruptcy records and stats
	 * Endpoint: GET /stats/bankruptcies
	 */
	public static async fetchBankruptcies(pageToken?: string): Promise<{
		success: boolean;
		stats?: {
			totalAmount: string;
			ifPayment: string;
			socialLoss: string;
			totalCount: string;
		};
		records?: Array<Record<string, unknown>>;
		meta?: { nextPage: string | null; totalRecords?: number };
	}> {
		try {
			const queryParams = new URLSearchParams();
			if (pageToken) {
				queryParams.set('page', pageToken);
			}

			const queryString = queryParams.toString();
			const fullUrl = createDataApiFullUrl(
				'/stats/bankruptcies',
				queryString ? `?${queryString}` : ''
			);

			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				stats: body.stats,
				records: body.records ?? [],
				meta: {
					nextPage: body.meta?.nextPage ?? null,
					totalRecords: body.meta?.totalRecords,
				},
			};
		} catch (e) {
			console.error('Error fetching bankruptcies from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch liquidation records and stats
	 * Endpoint: GET /stats/liquidations
	 */
	public static async fetchLiquidations(pageToken?: string): Promise<{
		success: boolean;
		stats?: {
			'24h': { count: string; amount: string };
			'30d': { count: string; amount: string };
		};
		records?: Array<Record<string, unknown>>;
		meta?: { nextPage: string | null };
	}> {
		try {
			const queryParams = new URLSearchParams();
			if (pageToken) {
				queryParams.set('page', pageToken);
			}

			const queryString = queryParams.toString();
			const fullUrl = createDataApiFullUrl(
				'/stats/liquidations',
				queryString ? `?${queryString}` : ''
			);

			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				stats: body.stats,
				records: body.records ?? [],
				meta: body.meta ?? { nextPage: null },
			};
		} catch (e) {
			console.error('Error fetching liquidations from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch rate history for a spot market
	 * Endpoint: GET /stats/:symbol/rateHistory/:type
	 */
	public static async fetchRateHistory(
		symbol: string,
		type: 'deposit' | 'borrow'
	): Promise<{
		success: boolean;
		rates?: Array<[number, string]>;
	}> {
		try {
			const fullUrl = createDataApiFullUrl(
				`/stats/${symbol}/rateHistory/${type}`,
				''
			);
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				rates: body.rates ?? [],
			};
		} catch (e) {
			console.error('Error fetching rate history from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch candles for a market
	 * Endpoint: GET /market/:symbol/candles/:resolution
	 */
	public static async fetchMarketCandles(params: {
		symbol: string;
		resolution: '1' | '5' | '15' | '60' | '240' | 'D' | 'W' | 'M';
		limit?: number;
		startTs?: number;
		endTs?: number;
	}): Promise<{
		success: boolean;
		status?: number;
		records?: Array<{
			ts: number;
			fillOpen: number;
			fillHigh: number;
			fillLow: number;
			fillClose: number;
			oracleOpen: number;
			oracleHigh: number;
			oracleLow: number;
			oracleClose: number;
			baseVolume: string;
			quoteVolume: string;
		}>;
	}> {
		try {
			// Clamp timestamps to current time - API rejects future timestamps
			const now = Math.floor(Date.now() / 1000);
			const clampedStartTs = params.startTs
				? Math.min(params.startTs, now)
				: undefined;
			const clampedEndTs = params.endTs
				? Math.min(params.endTs, now)
				: undefined;

			const queryParams = new URLSearchParams();
			if (params.limit) {
				queryParams.set('limit', params.limit.toString());
			}
			if (clampedStartTs) {
				queryParams.set('startTs', clampedStartTs.toString());
			}
			if (clampedEndTs) {
				queryParams.set('endTs', clampedEndTs.toString());
			}

			const queryString = queryParams.toString();
			const fullUrl = createDataApiFullUrl(
				`/market/${params.symbol}/candles/${params.resolution}`,
				queryString ? `?${queryString}` : ''
			);
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false, status: response.status };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				records: body.records ?? [],
			};
		} catch (e) {
			console.error('Error fetching market candles from data API:', e);
			return { success: false };
		}
	}

	/**
	 * Fetch insurance fund records for a spot market
	 * Endpoint: GET /market/:symbol/insuranceFund
	 */
	public static async fetchMarketInsuranceFundRecords(
		symbol: string,
		pageToken?: string
	): Promise<{
		success: boolean;
		records?: Array<Record<string, unknown>>;
		meta?: { nextPage: string | null };
	}> {
		try {
			const queryParams = new URLSearchParams();
			if (pageToken) {
				queryParams.set('page', pageToken);
			}

			const queryString = queryParams.toString();
			const fullUrl = createDataApiFullUrl(
				`/market/${symbol}/insuranceFund`,
				queryString ? `?${queryString}` : ''
			);
			const response = await fetch(fullUrl);

			if (!response.ok) {
				return { success: false };
			}

			const body = await response.json();

			return {
				success: body.success ?? true,
				records: body.records ?? [],
				meta: body.meta ?? { nextPage: null },
			};
		} catch (e) {
			console.error('Error fetching insurance fund records from data API:', e);
			return { success: false };
		}
	}
}

export default StatsApiClient;
